import {
  resolveBlockProseAlign,
  resolveBlockTextAlign,
  type BlockStyle,
} from "@/lib/cms-puck/block-style";
import { useBlockTextCss } from "@/hooks/use-block-style-css";
import {
  PROSE_ALIGN_CLASS,
  TEXT_ALIGN_CLASS,
} from "@/components/cms-puck/blocks/shared";
import { PreviewButton } from "@/components/cms-puck/blocks/button-blocks";
import {
  resolveCompositeInnerLayout,
  type LayoutAlign,
  type LayoutGap,
} from "@/lib/cms-puck/layout";

export const HEADING_SIZE_CLASS: Record<string, string> = {
  h1: "text-4xl",
  h2: "text-3xl",
  h3: "text-2xl",
  h4: "text-xl",
  h5: "text-lg",
  h6: "text-base",
};

/**
 * HeadingBlock — render du bloc Titre. Le traitement de texte (taille/alignement/
 * casse/italique/décoration/police/graisse) est appliqué INLINE sur l'élément
 * `<Hn>` via `useBlockTextCss` afin de BATTRE ses classes Tailwind
 * (`text-4xl`/`text-left`). Quand un réglage Design est « hérité » (null) :
 * - taille → classe selon le niveau ;
 * - alignement → champ `align` ;
 * - graisse → HÉRITAGE de la page (`--cms-page-font-weight`), PAS `font-semibold`
 *   (sinon le picker « Roboto — Light » affiché en héritage mentait vs un 600 forcé).
 * Le wrapper (withStyle) gère fond/marge/padding. Parité front : PuckHeading.
 */
export function HeadingBlock({
  level,
  text,
  align,
  style,
}: {
  level: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  text: React.ReactNode;
  align: "left" | "center" | "right";
  style?: BlockStyle;
}) {
  const Tag = level;
  const textCss = useBlockTextCss(style);
  const resolvedAlign = resolveBlockTextAlign(style, align);
  // Les classes `text-Nxl` embarquent une `line-height` FIXE (ex. 2.25rem) inadaptée
  // à une grande taille custom → les lignes se chevauchent, et cette line-height de
  // classe bat la règle `* { line-height: var(--cms-line-height) }`. On pose donc la
  // line-height EN INLINE (valeur du bloc si définie, sinon 1.1 = 110% du thème) →
  // elle bat la classe ET suit la taille. Parité front (PuckHeading).
  const lh = (textCss as Record<string, string | number | undefined>)[
    "--cms-line-height"
  ];
  // Cas responsive : la variable est portée par les règles du wrapper (pas
  // d'inline) → on la SUIT via var() ; fallback 1.1 (aucun ancêtre ne définit
  // cette variable en dehors des blocs, donc identique à l'ancien comportement).
  const css: React.CSSProperties = {
    ...textCss,
    lineHeight: lh ?? "var(--cms-line-height, 1.1)",
  };
  return (
    <Tag
      className={`tracking-tight ${HEADING_SIZE_CLASS[level]} ${TEXT_ALIGN_CLASS[resolvedAlign]}`}
      style={css}
    >
      {text}
    </Tag>
  );
}

/**
 * Bloc RichText : rendu STATIQUE sur le canvas (parité front via `CmsHtmlContent`).
 * L'édition du contenu se fait dans la SIDEBAR (champ `body` → `PuckRichTextEditor`)
 * et NON inline : Puck pose `user-select: none` sur ses blocs draggables, ce qui
 * rend la sélection de texte inline impossible. Le bloc reste enveloppé par
 * withStyle → la typo globale du Design cascade dans le HTML rendu.
 */
export function RichTextBlock({
  body,
  align = "left",
  style,
}: {
  body?: string;
  align?: "left" | "center" | "right";
  style?: BlockStyle;
}) {
  const resolvedAlign = resolveBlockProseAlign(style, align);
  return (
    <div className={`w-full min-w-0 ${PROSE_ALIGN_CLASS[resolvedAlign]}`}>
      <div
        className="cms-html-content cms-page-font-inherit max-w-none"
        dangerouslySetInnerHTML={{ __html: body ?? "" }}
      />
    </div>
  );
}

/** CtaBlock — aperçu éditeur (titre + texte + bouton). Parité front. */
export function CtaBlock({
  title,
  text,
  buttonLabel,
  variant,
  align,
  gap,
  gapCustomPx,
  style,
}: {
  title: React.ReactNode;
  text: React.ReactNode;
  buttonLabel?: string;
  variant: "primary" | "secondary" | "outline";
  align: "left" | "center";
  gap?: LayoutGap | null;
  gapCustomPx?: number | null;
  style?: BlockStyle;
}) {
  const textCss = useBlockTextCss(style);
  const resolvedAlign = resolveBlockTextAlign(style, align);
  const inner = resolveCompositeInnerLayout({
    gap,
    gapCustomPx,
    withAlignItems: false,
  });
  return (
    <section
      className={`mx-auto flex max-w-3xl flex-col ${inner.className} ${TEXT_ALIGN_CLASS[resolvedAlign]}`}
      style={inner.style}
    >
      <h2 className="text-3xl font-semibold tracking-tight" style={textCss}>
        {title}
      </h2>
      {text && (
        <p className="text-lg text-slate-600" style={textCss}>
          {text}
        </p>
      )}
      {buttonLabel && (
        // Bloc simple (pas de flex) : le bouton inline suit le text-align
        // hérité, donc aussi les surcharges tablette / mobile.
        <div>
          <PreviewButton label={buttonLabel} variant={variant} />
        </div>
      )}
    </section>
  );
}

/** Classes de ton du Bandeau (parité BO ↔ front). */
export const BANNER_TONE_CLASS: Record<"neutral" | "accent" | "dark", string> = {
  neutral: "bg-slate-100 text-slate-800",
  accent: "bg-blue-50 text-blue-900",
  dark: "bg-slate-900 text-white",
};

/** BannerBlock — bandeau compact (message + lien). Parité front. */
export function BannerBlock({
  text,
  linkLabel,
  tone,
  gap,
  gapCustomPx,
  alignItems,
  style,
}: {
  text: React.ReactNode;
  linkLabel?: string;
  tone: "neutral" | "accent" | "dark";
  gap?: LayoutGap | null;
  gapCustomPx?: number | null;
  alignItems?: LayoutAlign | null;
  style?: BlockStyle;
}) {
  const textCss = useBlockTextCss(style);
  const inner = resolveCompositeInnerLayout({
    gap,
    gapCustomPx,
    alignItems,
    alignFallback: "center",
  });
  return (
    <div
      className={`flex flex-wrap justify-center text-center text-sm ${inner.className} ${BANNER_TONE_CLASS[tone]}`}
      style={inner.style}
    >
      <span style={textCss}>{text}</span>
      {linkLabel && (
        <span className="font-semibold underline" style={textCss}>
          {linkLabel}
        </span>
      )}
    </div>
  );
}

/**
 * QuoteBlock — citation + auteur. Traitement du texte (onglet Design) appliqué
 * INLINE sur la citation ET l'auteur pour battre leurs classes Tailwind. Parité front.
 */
export function QuoteBlock({
  text,
  author,
  align,
  gap,
  gapCustomPx,
  style,
}: {
  text: React.ReactNode;
  author: React.ReactNode;
  align: "left" | "center" | "right";
  gap?: LayoutGap | null;
  gapCustomPx?: number | null;
  style?: BlockStyle;
}) {
  const textCss = useBlockTextCss(style);
  const resolvedAlign = resolveBlockTextAlign(style, align);
  const inner = resolveCompositeInnerLayout({
    gap,
    gapCustomPx,
    withAlignItems: false,
  });
  return (
    <figure
      className={`mx-auto flex max-w-3xl flex-col ${inner.className} ${TEXT_ALIGN_CLASS[resolvedAlign]}`}
      style={inner.style}
    >
      <blockquote
        className="border-l-4 border-slate-300 pl-4 text-xl italic text-slate-700"
        style={textCss}
      >
        {text}
      </blockquote>
      {author && (
        <figcaption
          className="text-sm font-medium text-slate-500"
          style={textCss}
        >
          — {author}
        </figcaption>
      )}
    </figure>
  );
}
